import { v4 as uuid } from 'uuid';

import { BRAND_NAME } from '~/common/constants/brand';
import { URL_REGEXP } from '~/common/constants/regexp';

export const getProvider = (intl) => ({
  id: uuid(),
  name: intl.formatMessage({
    id: 'SamlFields.providerSamlInformation',
    defaultMessage: 'Provider SAML Information',
    description: 'Title for SAML provider information section',
  }),
  title: intl.formatMessage(
    {
      id: 'SamlFields.evisortServiceProviderDetails',
      defaultMessage: '{brandName} service provider details',
      description: 'Title for the service provider details section',
    },
    { brandName: BRAND_NAME },
  ),
  description: intl.formatMessage({
    id: 'SamlFields.enterFieldsIntoIdp',
    defaultMessage: 'Enter the following fields into your IDP.',
    description: 'Instructions for entering fields into IDP',
  }),
  instructions: [
    {
      id: 'saml_sp_url',
      title: intl.formatMessage({
        id: 'SamlFields.assertionConsumerServiceUrl',
        defaultMessage: 'Assertion consumer service (ACS) URL',
        description: 'Title for ACS URL field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.acsUrlDescription',
        defaultMessage:
          'The ACS URL tells the IDP where to redirect an authenticated user after sign-in.',
        description: 'Description of what ACS URL does',
      }),
    },
    {
      id: 'sp_id',
      title: intl.formatMessage({
        id: 'SamlFields.serviceProviderEntityId',
        defaultMessage: 'Service provider (SP) entity ID',
        description: 'Title for service provider entity ID',
      }),
      description: intl.formatMessage(
        {
          id: 'SamlFields.spEntityIdDescription',
          defaultMessage:
            "This is {brandName}'s unique identifier and does not change per implementation.",
          description: 'Description of service provider entity ID',
        },
        { brandName: BRAND_NAME },
      ),
    },
    {
      id: 'preferred_settings',
      title: intl.formatMessage({
        id: 'SamlFields.additionalSettings',
        defaultMessage: 'Additional settings',
        description: 'Title for additional settings section',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.additionalSettingsDescription',
        defaultMessage:
          'This section is optional and only applies if your IDP supports configuring verification certificates or signed requests.',
        description: 'Description of additional settings section',
      }),
    },
    {
      id: 'provider_details_4',
      title: intl.formatMessage({
        id: 'SamlFields.downloadableImageOptional',
        defaultMessage: 'Downloadable image (Optional)',
        description: 'Title for downloadable image section',
      }),
      description: intl.formatMessage(
        {
          id: 'SamlFields.uploadEvisortLogo',
          defaultMessage:
            'Upload the {brandName} logo into your IDP.\n\n[View {brandName} logo](https://cdn-assets-cloud.frontify.com/s3/frontify-cloud-files-us/eyJwYXRoIjoiZnJvbnRpZnlcL2ZpbGVcL1lMbVYyVGh0ajJnelhEUW5IODlnLnBuZyJ9:frontify:FowozH95lCVpdRUE9VWBMR2BdHRjVod5vNpo9wfX2Fw)',
          description: 'Instructions for uploading the brand logo with link',
        },
        { brandName: BRAND_NAME },
      ),
    },
  ],
  configs: [
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.assertionConsumerServiceUrl',
        defaultMessage: 'Assertion consumer service (ACS) URL',
        description: 'Title for ACS URL field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.acsUrlAlsoKnownAs',
        defaultMessage: 'This is also known as Reply URL.',
        description: 'Alternative name for ACS URL',
      }),
      fieldName: 'saml_sp_url',
      copiable: true,
      disabled: true,
      visible: true,
      required: true,
      field: {
        id: uuid(),
        type: 'SHORT_RESPONSE',
        value: null,
      },
    },
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.serviceProviderEntityId',
        defaultMessage: 'Service provider (SP) entity ID',
        description: 'Title for service provider entity ID',
      }),
      description: '',
      fieldName: 'sp_id',
      copiable: true,
      disabled: true,
      visible: true,
      required: true,
      field: {
        id: uuid(),
        type: 'SHORT_RESPONSE',
        value: null,
      },
    },
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.additionalSettings',
        defaultMessage: 'Additional settings',
        description: 'Title for additional settings section',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.enterDetailsIfSpecified',
        defaultMessage: 'Enter these details if specified by your IDP.',
        description: 'Instructions for additional settings',
      }),
      fieldName: 'preferred_settings',
      copiable: false,
      disabled: false,
      visible: true,
      required: false,
      field: {
        id: uuid(),
        type: 'STATIC',
        value: [
          {
            title: intl.formatMessage({
              id: 'SamlFields.assertionSignature',
              defaultMessage: 'Assertion Signature',
              description: 'Label for assertion signature setting',
            }),
            value: intl.formatMessage({
              id: 'SamlFields.signed',
              defaultMessage: 'Signed',
              description: 'Value for signed assertion',
            }),
          },
          {
            title: intl.formatMessage({
              id: 'SamlFields.signatureAlgorithm',
              defaultMessage: 'Signature Algorithm',
              description: 'Label for signature algorithm setting',
            }),
            value: 'RSA-SHA256',
          },
        ],
      },
    },
  ],
});

export const getConfigs = (intl) => ({
  id: uuid(),
  name: intl.formatMessage({
    id: 'SamlFields.samlConfiguration',
    defaultMessage: 'SAML Configuration',
    description: 'Title for SAML configuration section',
  }),
  title: intl.formatMessage({
    id: 'SamlFields.identityProviderDetails',
    defaultMessage: 'Identity provider details',
    description: 'Title for identity provider details section',
  }),
  description: intl.formatMessage({
    id: 'SamlFields.retrieveFieldsFromIdp',
    defaultMessage: 'Retrieve the following fields from your IDP.',
    description: 'Instructions for retrieving fields from IDP',
  }),
  instructions: [
    {
      id: 'idp_name',
      title: intl.formatMessage({
        id: 'SamlFields.idpName',
        defaultMessage: 'IDP name',
        description: 'Title for IDP name field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.idpNameDescription',
        defaultMessage:
          'The name of your identity provider (Eg: Azure, Okta, Google) that users will be signing in with. The name entered here is for display purposes only.',
        description: 'Description of IDP name field with examples',
      }),
    },
    {
      id: 'sso_url',
      title: intl.formatMessage({
        id: 'SamlFields.ssoUrl',
        defaultMessage: 'SSO URL',
        description: 'Title for SSO URL field',
      }),
      description: intl.formatMessage(
        {
          id: 'SamlFields.ssoUrlDescription',
          defaultMessage:
            'This is the URL where {brandName} will redirect users to sign in to your IDP.',
          description: 'Description of SSO URL field',
        },
        { brandName: BRAND_NAME },
      ),
    },
    {
      id: 'idp_id',
      title: intl.formatMessage({
        id: 'SamlFields.idpId',
        defaultMessage: 'IDP ID',
        description: 'Title for IDP ID field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.idpIdDescription',
        defaultMessage: "This is your IDP's unique identifier.",
        description: 'Description of IDP ID field',
      }),
    },
    {
      id: 'idp_metadata_url',
      title: intl.formatMessage({
        id: 'SamlFields.idpMetadataUrl',
        defaultMessage: 'IDP metadata URL',
        description: 'Title for IDP metadata URL field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.idpMetadataUrlDescription',
        defaultMessage:
          'Input this if the X.509 Cert is variable or rotating. Note: In some cases, if you provide this then you do not need to enter the X.509 certificate. However, this depends on your setup and it may be required to save the configuration.',
        description: 'Description of IDP metadata URL field',
      }),
    },
    {
      id: 'x509_cert',
      title: intl.formatMessage({
        id: 'SamlFields.x509Cert',
        defaultMessage: 'X.509 cert',
        description: 'Title for X.509 certificate field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.x509CertDescription',
        defaultMessage:
          'Find and open the metadata XML file in your IDP. Copy and paste the X.509 Base64 certificate code here. You can find the code between BEGIN CERTIFICATE and END CERTIFICATE.',
        description: 'Description of X.509 certificate field',
      }),
    },
    {
      id: 'auth_context_class',
      title: intl.formatMessage({
        id: 'SamlFields.authenticationContextClass',
        defaultMessage: 'Authentication Context Class',
        description: 'Title for authentication context class field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.authContextClassDescription',
        defaultMessage:
          "Identifies the SAML authentication context class for the assertion's authentication statement. The options are: Password Protected Transport (default), None, Password, Unspecified, TLS Client, X.509 Certificate, and Kerberos. Note: If you are using Azure AD, you can set this to None.",
        description: 'Description of authentication context class field',
      }),
    },
  ],
  configs: [
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.idpName',
        defaultMessage: 'IDP name',
        description: 'Title for IDP name field',
      }),
      fieldName: 'idp_name',
      description: intl.formatMessage({
        id: 'SamlFields.displayPurposesOnly',
        defaultMessage: 'This is used for display purposes only.',
        description: 'Note about display purposes',
      }),
      copiable: false,
      disabled: false,
      visible: true,
      required: true,
      field: {
        id: uuid(),
        type: 'SHORT_RESPONSE',
        value: null,
      },
    },
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.ssoUrl',
        defaultMessage: 'SSO URL',
        description: 'Title for SSO URL field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.ssoUrlAlsoKnownAs',
        defaultMessage: 'This is also known as the SAML 2.0 Endpoint.',
        description: 'Alternative name for SSO URL',
      }),
      fieldName: 'sso_url',
      copiable: false,
      disabled: false,
      visible: true,
      required: true,
      pattern: URL_REGEXP,
      patternError: intl.formatMessage({
        id: 'SamlFields.enterValidUrl',
        defaultMessage: 'Enter a valid URL.',
        description: 'Error message for invalid URL',
      }),
      field: {
        id: uuid(),
        type: 'SHORT_RESPONSE',
        value: null,
      },
    },
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.idpId',
        defaultMessage: 'IDP ID',
        description: 'Title for IDP ID field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.idpIdAlsoKnownAs',
        defaultMessage:
          'This is also known as the Identity Provider Issuer or Entity ID.',
        description: 'Alternative names for IDP ID',
      }),
      fieldName: 'idp_id',
      copiable: false,
      disabled: false,
      visible: true,
      required: true,
      field: {
        id: uuid(),
        type: 'SHORT_RESPONSE',
        value: null,
      },
    },
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.idpMetadataUrl',
        defaultMessage: 'IDP metadata URL',
        description: 'Title for IDP metadata URL field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.enterIfX509Variable',
        defaultMessage: 'Enter this if the X.509 Cert is variable or rotating.',
        description: 'Instructions for when to enter metadata URL',
      }),
      fieldName: 'idp_metadata_url',
      copiable: false,
      disabled: false,
      visible: true,
      required: false,
      pattern: URL_REGEXP,
      patternError: intl.formatMessage({
        id: 'SamlFields.enterValidUrl',
        defaultMessage: 'Enter a valid URL.',
        description: 'Error message for invalid URL',
      }),
      field: {
        id: uuid(),
        type: 'SHORT_RESPONSE',
        value: null,
      },
    },
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.x509Cert',
        defaultMessage: 'X.509 cert',
        description: 'Title for X.509 certificate field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.copyPasteCertificate',
        defaultMessage:
          "Copy and paste the certificate. You can find this in your IDP's metadata.xml file.",
        description: 'Instructions for copying certificate',
      }),
      fieldName: 'x509_cert',
      copiable: false,
      disabled: false,
      visible: true,
      required: true,
      field: {
        id: uuid(),
        type: 'LONG_RESPONSE',
        value: null,
      },
    },
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.authenticationContextClass',
        defaultMessage: 'Authentication Context Class',
        description: 'Title for authentication context class field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.enterIfSpecifiedByIdp',
        defaultMessage: 'Enter this if specified by your IDP.',
        description: 'Instructions for authentication context class',
      }),
      fieldName: 'auth_context_class',
      copiable: false,
      disabled: false,
      visible: true,
      required: false,
      field: {
        id: uuid(),
        options: [
          {
            label: intl.formatMessage({
              id: 'SamlFields.passwordProtectedTransport',
              defaultMessage: 'Password Protected Transport',
              description: 'Authentication context class option',
            }),
            value: 'password_protected_transport',
            descriptions: [],
          },
          {
            label: intl.formatMessage({
              id: 'SamlFields.none',
              defaultMessage: 'None',
              description: 'Authentication context class option',
            }),
            value: 'none',
            descriptions: [],
          },
          {
            label: intl.formatMessage({
              id: 'SamlFields.password',
              defaultMessage: 'Password',
              description: 'Authentication context class option',
            }),
            value: 'password',
            descriptions: [],
          },
          {
            label: intl.formatMessage({
              id: 'SamlFields.unspecified',
              defaultMessage: 'Unspecified',
              description: 'Authentication context class option',
            }),
            value: 'unspecified',
            descriptions: [],
          },
          {
            label: intl.formatMessage({
              id: 'SamlFields.tlsClient',
              defaultMessage: 'TLS Client',
              description: 'Authentication context class option',
            }),
            value: 'tls_client',
            descriptions: [],
          },
          {
            label: intl.formatMessage({
              id: 'SamlFields.x509Certificate',
              defaultMessage: 'X.509 Certificate',
              description: 'Authentication context class option',
            }),
            value: 'x509',
            descriptions: [],
          },
          {
            label: intl.formatMessage({
              id: 'SamlFields.x509Multifactor',
              defaultMessage: 'X.509 Multifactor',
              description: 'Authentication context class option',
            }),
            value: 'x509_multifactor',
            descriptions: [],
          },
          {
            label: intl.formatMessage({
              id: 'SamlFields.kerberos',
              defaultMessage: 'Kerberos',
              description: 'Authentication context class option',
            }),
            value: 'kerberos',
            descriptions: [],
          },
        ],
        type: 'SINGLE_SELECT',
        value: {
          label: intl.formatMessage({
            id: 'SamlFields.passwordProtectedTransport',
            defaultMessage: 'Password Protected Transport',
            description: 'Authentication context class option',
          }),
          value: 'password_protected_transport',
          descriptions: [],
        },
      },
    },
  ],
});

export const getAttributeMapping = (intl) => ({
  id: uuid(),
  name: intl.formatMessage({
    id: 'SamlFields.userAttributeMapping',
    defaultMessage: 'User attribute mapping',
    description: 'Title for user attribute mapping section',
  }),
  title: intl.formatMessage({
    id: 'SamlFields.userAttributeMapping',
    defaultMessage: 'User attribute mapping',
    description: 'Title for user attribute mapping section',
  }),
  instructions: [
    {
      id: 'attributes',
      title: intl.formatMessage({
        id: 'SamlFields.supportedUserAttributes',
        defaultMessage: 'Supported User Attributes',
        description: 'Title for supported user attributes section',
      }),
      description: intl.formatMessage(
        {
          id: 'SamlFields.attributeMappingDescription',
          defaultMessage:
            'Attribute mappings are used to control which user profile details are exchanged between your IDP and {brandName} during the user provisioning process. {brandName} requires the Email, First name, and Last name attributes to create and sign in a user. Note: The attributes can be named however you want, just ensure they match between the IDP side and the {brandName} side. See guides for these IDPs: - [View Okta example](https://doc.workday.com/evisort/en-us/contract-management-and-document-intelligence/technical-guides/reference--okta-sso-configuration.html?toc=10.5) - [View Microsoft Entra ID example](https://doc.workday.com/evisort/en-us/contract-management-and-document-intelligence/technical-guides/reference--sso-microsoft-azure-configuration.html?toc=10.4) - [View OneLogin example](https://doc.workday.com/evisort/en-us/contract-management-and-document-intelligence/technical-guides/reference--onelogin-sso-configuration.html?toc=10.6)',
          description: 'Description of attribute mapping with links to guides',
        },
        { brandName: BRAND_NAME },
      ),
    },
    {
      id: 'custom_attribute_map',
      title: intl.formatMessage({
        id: 'SamlFields.customUserAttributes',
        defaultMessage: 'Custom user attributes',
        description: 'Title for custom user attributes section',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.customAttributesDescription',
        defaultMessage:
          'You also have the option to add and map additional attributes that will be visible on the Users table. This is useful if your organization uses other attributes to categorize or define users. Example attributes: - Security group - User ID - Cost center',
        description: 'Description of custom attributes with examples',
      }),
    },
  ],
  configs: [
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.supportedUserAttributesField',
        defaultMessage: 'Supported user attributes',
        description: 'Title for supported user attributes field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.copyAttributeNames',
        defaultMessage:
          'Copy the attribute names or URLs from your IDP and paste them here. You can use any attribute name, but ensure the name here matches the name in your IDP.',
        description: 'Instructions for copying attribute names',
      }),
      fieldName: 'attributes',
      copiable: false,
      disabled: false,
      visible: true,
      required: true,
      field: {
        id: uuid(),
        type: 'ATTRIBUTES',
        value: {
          email: null,
          firstName: null,
          lastName: null,
          jobTitle: null,
          department: null,
          role: null,
        },
      },
    },
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.customUserAttributesOptional',
        defaultMessage: 'Custom user attributes (optional)',
        description: 'Title for custom user attributes field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.copyAttributeNames',
        defaultMessage:
          'Copy the attribute names or URLs from your IDP and paste them here. You can use any attribute name, but ensure the name here matches the name in your IDP.',
        description: 'Instructions for copying attribute names',
      }),
      copiable: false,
      disabled: false,
      visible: true,
      required: false,
      fieldName: 'custom_attribute_map',
      field: {
        id: uuid(),
        type: 'CUSTOM_ATTRIBUTES',
        value: {},
      },
    },
  ],
});

export const getEnforce = (intl) => ({
  id: uuid(),
  name: intl.formatMessage({
    id: 'SamlFields.enforcementOptions',
    defaultMessage: 'Enforcement Options',
    description: 'Title for enforcement options section',
  }),
  title: intl.formatMessage({
    id: 'SamlFields.ssoEnforcement',
    defaultMessage: 'SSO Enforcement',
    description: 'Title for SSO enforcement section',
  }),
  instructions: [
    {
      id: 'is_enabled',
      title: intl.formatMessage({
        id: 'SamlFields.ssoEnablementInstruction',
        defaultMessage: 'SSO enablement',
        description: 'Title for SSO enablement instruction',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.ssoEnablementDescription',
        defaultMessage:
          'When enable SSO is: - Checked: the single sign-on option shows on the user sign in page and user attributes sync with your IDP - Unchecked: the single sign-on option does not show on the user sign in page and user attributes do not sync with your IDP',
        description: 'Description of SSO enablement options',
      }),
    },
    {
      id: 'policy',
      title: intl.formatMessage({
        id: 'SamlFields.ssoEnforcedUsersInstruction',
        defaultMessage: 'SSO enforced users',
        description: 'Title for SSO enforced users instruction',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.ssoEnforcedUsersDescription',
        defaultMessage:
          'This can be set to: - None: users are not required to sign in via SSO - All Users: users (all roles) are required to sign in via SSO - Non-Admin users: users (exlcuding Admins) are required to sign in via SSO - Select Domains: select which domains sign in via SSO',
        description: 'Description of SSO enforcement policy options',
      }),
    },
    {
      id: 'default_role',
      title: intl.formatMessage({
        id: 'SamlFields.defaultRole',
        defaultMessage: 'Default role',
        description: 'Title for default role instruction',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.defaultRoleDescription',
        defaultMessage:
          'Newly provisioned users who sign in for the first time through SSO are automatically assigned a default role. You can change this default role at any time. Note: To assign a different role to users who have already signed in, navigate to the Users page.',
        description: 'Description of default role assignment',
      }),
    },
  ],
  configs: [
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.ssoEnablement',
        defaultMessage: 'SSO enablement',
        description: 'Title for SSO enablement field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.ssoEnablementToggleDescription',
        defaultMessage:
          'This will turn on the single sign-on option on the sign in page and sync user attributes with your IDP.',
        description: 'Description for SSO enablement toggle',
      }),
      fieldName: 'is_enabled',
      copiable: false,
      disabled: false,
      visible: true,
      required: false,
      field: {
        id: uuid(),
        type: 'TOGGLE',
        name: intl.formatMessage({
          id: 'SamlFields.enableSso',
          defaultMessage: 'Enable SSO',
          description: 'Label for enable SSO toggle',
        }),
        description: intl.formatMessage({
          id: 'SamlFields.usersWillBeAbleToLogin',
          defaultMessage: 'Users will be able to log in via the SSO provider.',
          description: 'Description for SSO toggle functionality',
        }),
        value: true,
      },
    },
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.ssoEnforcedUsers',
        defaultMessage: 'SSO enforced users',
        description: 'Title for SSO enforced users field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.selectWhichUsersMustUseSso',
        defaultMessage: 'Select which users must use SSO to sign in.',
        description: 'Description for SSO enforcement selection',
      }),
      fieldName: 'policy',
      copiable: false,
      disabled: false,
      visible: true,
      required: false,
      field: {
        id: uuid(),
        options: [
          {
            label: intl.formatMessage({
              id: 'SamlFields.notEnforced',
              defaultMessage: 'Not Enforced',
              description: 'SSO enforcement policy option',
            }),
            value: 'enforce_none',
            descriptions: [
              intl.formatMessage({
                id: 'SamlFields.notEnforcedDescription',
                defaultMessage:
                  'Users will have the option to log in via the SSO provider or using their username and password.',
                description: 'Description for not enforced SSO policy',
              }),
            ],
          },
          {
            label: intl.formatMessage({
              id: 'SamlFields.enforcedForAllUsers',
              defaultMessage: 'Enforced for All Users',
              description: 'SSO enforcement policy option',
            }),
            value: 'enforce_all',
            descriptions: [
              intl.formatMessage({
                id: 'SamlFields.enforcedForAllUsersDescription1',
                defaultMessage:
                  'All users will have to log in using the SSO provider.',
                description: 'First description for enforced all users policy',
              }),
              intl.formatMessage({
                id: 'SamlFields.enforcedForAllUsersDescription2',
                defaultMessage:
                  'Note: Users with email addresses that end with "@evisort.com" will be able to log into the platform using their email and password to provide support to your organization.',
                description: 'Second description for enforced all users policy',
              }),
            ],
          },
          {
            label: intl.formatMessage({
              id: 'SamlFields.enforcedForAllNonAdminUsers',
              defaultMessage: 'Enforced for All Non-Admin Users',
              description: 'SSO enforcement policy option',
            }),
            value: 'enforce_all_non_admin',
            descriptions: [
              intl.formatMessage({
                id: 'SamlFields.enforcedForAllNonAdminUsersDescription',
                defaultMessage:
                  'Only Admins will be able to log into the platform using their email and password. All other users will have to log in using the SSO provider.',
                description: 'Description for enforced non-admin users policy',
              }),
            ],
          },
          {
            label: intl.formatMessage({
              id: 'SamlFields.enforcedForSelectDomains',
              defaultMessage: 'Enforced for Select Domains',
              description: 'SSO enforcement policy option',
            }),
            value: 'enforce_domains',
            descriptions: [
              intl.formatMessage({
                id: 'SamlFields.enforcedForSelectDomainsDescription1',
                defaultMessage:
                  'All users with email addresses from the provided list of domains must log in using SSO.',
                description:
                  'First description for enforced select domains policy',
              }),
              intl.formatMessage({
                id: 'SamlFields.enforcedForSelectDomainsDescription2',
                defaultMessage:
                  'Note: Users with email addresses that end with "evisort.com" will be able to log into the platform using their email and password to provide support to your organization.',
                description:
                  'Second description for enforced select domains policy',
              }),
            ],
          },
        ],
        description: intl.formatMessage({
          id: 'SamlFields.notEnforcedDescription',
          defaultMessage:
            'Users will have the option to log in via the SSO provider or using their username and password.',
          description: 'Description for not enforced SSO policy',
        }),
        type: 'SINGLE_SELECT',
        value: {
          label: intl.formatMessage({
            id: 'SamlFields.enforcedForAllNonAdminUsers',
            defaultMessage: 'Enforced for All Non-Admin Users',
            description: 'SSO enforcement policy option',
          }),
          value: 'enforce_all_non_admin',
          descriptions: [
            intl.formatMessage({
              id: 'SamlFields.enforcedForAllNonAdminUsersDescription',
              defaultMessage:
                'Only Admins will be able to log into the platform using their email and password. All other users will have to log in using the SSO provider.',
              description: 'Description for enforced non-admin users policy',
            }),
          ],
        },
      },
    },
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.enforcedDomains',
        defaultMessage: 'Enforced domains',
        description: 'Title for enforced domains field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.enterEmailAddressesForSso',
        defaultMessage: 'Enter email addresses that must log in via SSO.',
        description: 'Description for enforced domains field',
      }),
      fieldName: 'allowed_domains',
      copiable: false,
      disabled: false,
      visible: false,
      required: false,
      field: {
        id: uuid(),
        type: 'MULTI_SELECT',
        options: [],
        value: [],
      },
    },
    {
      id: uuid(),
      title: intl.formatMessage({
        id: 'SamlFields.defaultRoleInstructions',
        defaultMessage: 'Default Role',
        description: 'Title for default role field',
      }),
      description: intl.formatMessage({
        id: 'SamlFields.assignRoleToNewUsers',
        defaultMessage: 'Assign a role to new users who sign in via SSO.',
        description: 'Description for default role assignment',
      }),
      copiable: false,
      disabled: false,
      visible: true,
      required: false,
      fieldName: 'default_role',
      field: {
        id: uuid(),
        type: 'ROLE_SELECT',
        options: [],
        value: null,
      },
    },
  ],
});
